import { ViewResponse } from '../models/viewResponse/ViewResponse';
import { getConfig } from '../api/resource';
import { fillIdentity } from './utils/fillIdentity/fillIdentityEngine';
import {
  clearStorage,
  clearStorageByPrefix,
  getWithTTL,
  removeFromStorage,
  setWithTTL,
  STORAGE_PREFIX,
} from './utils/storageTTL';

/**
 * Время жизни записи конфигурации в локальном кэше.
 */
const CONFIG_CACHE_TTL = 24 * 60 * 60 * 1000;

/**
 * Максимальный период отсутствия использования конфигурации,
 * после которого запись удаляется при очистке кэша.
 */
const CONFIG_STALE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

/**
 * Метаданные сохранённой конфигурации.
 */
interface CachedConfigMeta {
  /** Ключ модуля, которому принадлежит конфигурация. */
  moduleName: string;
  /** Хеш модуля на момент сохранения конфигурации. */
  moduleHash: string;
  /** Тип сущности представления. */
  type: string;
  /** Имя конфигурации представления. */
  configName: string;
  /** Время создания записи в кэше. */
  savedAt: number;
  /** Время последнего использования записи. */
  lastUsedAt: number;
}

/**
 * Запись конфигурации представления в локальном кэше.
 */
interface CachedConfig {
  /** Метаданные кэшированной конфигурации. */
  meta: CachedConfigMeta;
  /** Исходная конфигурация представления. */
  config: ViewResponse;
}

/**
 * Параметры запроса конфигурации представления.
 */
export interface ConfigRequestParams {
  /**
   * Имя конфигурации.
   */
  configName: string;
  /**
   * Тип сущности.
   */
  type: string;
  /**
   * Идентификатор экземпляра сущности.
   */
  id?: string;
}

/**
 * Дополнительные параметры получения конфигурации из кэша.
 */
export interface ConfigCacheOptions {
  /**
   * Не выполнять подстановку текущего identity в конфигурацию.
   */
  skipFill?: boolean;
}

/**
 * Запросы конфигураций, которые в данный момент выполняются.
 * Используется для deduplication одинаковых параллельных запросов.
 */
const pendingRequests = new Map<string, Promise<ViewResponse>>();

/**
 * Формирует ключ записи конфигурации в локальном кэше.
 *
 * Один и тот же конфиг используется для разных entity id,
 * поэтому id намеренно не входит в cache key.
 */
const buildCacheKey = (moduleKey: string, type: string, configName: string): string =>
  `${moduleKey}:${type}:${configName}`;

/**
 * Формирует ключ выполняющегося backend-запроса.
 *
 * В отличие от cache key, id входит в request key,
 * поскольку backend может вернуть разные конфигурации
 * для разных экземпляров.
 */
const buildRequestKey = (moduleKey: string, params: ConfigRequestParams): string =>
  [moduleKey, params.type, params.configName, params.id ?? ''].join(':');

/**
 * Проверяет актуальность записи кэша относительно текущего хеша модуля.
 *
 * Если сохранённая запись не содержит moduleHash, она считается
 * совместимой со старой версией формата кэша.
 */
const isValidCacheEntry = (cached: CachedConfig, currentModuleHash?: string): boolean => {
  if (currentModuleHash && cached.meta.moduleHash && cached.meta.moduleHash !== currentModuleHash) {
    return false;
  }

  return true;
};

/**
 * Создаёт структуру записи для сохранения конфигурации в кэш.
 */
const createCachedConfig = (
  moduleKey: string,
  params: ConfigRequestParams,
  config: ViewResponse,
  moduleHash?: string,
): CachedConfig => {
  const now = Date.now();

  return {
    meta: {
      moduleName: moduleKey,
      moduleHash: moduleHash ?? '',
      type: params.type,
      configName: params.configName,
      savedAt: now,
      lastUsedAt: now,
    },
    config,
  };
};

/**
 * Обновляет время последнего использования записи кэша.
 * Запись сохраняется заново, чтобы одновременно продлить TTL
 */
const updateLastUsedAt = (cacheKey: string, cached: CachedConfig): void => {
  const updated: CachedConfig = {
    ...cached,
    meta: {
      ...cached.meta,
      lastUsedAt: Date.now(),
    },
  };

  setWithTTL(cacheKey, updated, CONFIG_CACHE_TTL);
};

/**
 * Выполняет подстановку текущего identity в конфигурацию.
 *
 * Кэш содержит исходную конфигурацию без привязки к конкретному id.
 * Поэтому identity подставляется только непосредственно перед
 * передачей конфигурации потребителю.
 */
const applyIdentity = (
  config: ViewResponse,
  id: string | undefined,
  skipFill?: boolean,
): ViewResponse => {
  if (skipFill || !config.requiresIdentityContextFill) {
    return config;
  }

  return fillIdentity(config, id ?? null);
};

/**
 * Выполняет запрос конфигурации к backend с deduplication.
 *
 * Если такой же запрос уже выполняется, возвращается существующий Promise.
 * Это предотвращает несколько одинаковых HTTP-запросов при одновременном
 * запуске нескольких эффектов или обработчиков.
 */
const requestFreshConfig = (
  moduleKey: string,
  params: ConfigRequestParams,
  requestKey: string,
): Promise<ViewResponse> => {
  const existingRequest = pendingRequests.get(requestKey);

  if (existingRequest) {
    return existingRequest;
  }

  const request = getConfig(moduleKey, {
    configName: params.configName,
    id: params.id,
    type: params.type,
  }).finally(() => {
    pendingRequests.delete(requestKey);
  });

  pendingRequests.set(requestKey, request);

  return request;
};

/**
 * Получает конфигурацию представления с использованием локального кэша.
 *
 * Алгоритм:
 *
 * 1. Проверяется наличие конфигурации в localStorage.
 * 2. Проверяется TTL и module hash.
 * 3. При попадании обновляется `lastUsedAt`.
 * 4. Для конфигураций с `requiresIdentityContextFill` создаётся
 *    отдельная копия с актуальным entity id.
 * 5. При отсутствии записи выполняется backend-запрос.
 * 6. Конфигурация сохраняется в кэш только при `supportsCaching`.
 * 7. Одинаковые параллельные backend-запросы объединяются.
 */
export async function getConfigWithCache(
  moduleKey: string,
  params: ConfigRequestParams,
  currentModuleHash?: string,
  options?: ConfigCacheOptions,
): Promise<ViewResponse> {
  const cacheKey = buildCacheKey(moduleKey, params.type, params.configName);

  const cached = getWithTTL<CachedConfig>(cacheKey, CONFIG_CACHE_TTL);

  if (cached) {
    if (isValidCacheEntry(cached, currentModuleHash)) {
      updateLastUsedAt(cacheKey, cached);

      return applyIdentity(cached.config, params.id, options?.skipFill);
    }

    removeFromStorage(cacheKey);
  }

  const requestKey = buildRequestKey(moduleKey, params);

  const freshConfig = await requestFreshConfig(moduleKey, params, requestKey);

  if (freshConfig.supportsCaching) {
    const cachedConfig = createCachedConfig(moduleKey, params, freshConfig, currentModuleHash);

    setWithTTL(cacheKey, cachedConfig, CONFIG_CACHE_TTL);
  }

  return applyIdentity(freshConfig, params.id, options?.skipFill);
}

/**
 * Инвалидирует все кэшированные конфигурации указанного модуля.
 */
export function invalidateModuleCache(moduleName: string): void {
  clearStorageByPrefix(`${moduleName}:`);
}

/**
 * Полностью очищает кэш конфигураций представлений.
 *
 * Используется, например, при logout/login,
 * когда старые конфигурации больше не должны использоваться.
 */
export function clearConfigCache(): void {
  clearStorage();
}

/**
 * Удаляет давно не использовавшиеся конфигурации.
 *
 * Функция предназначена для периодической очистки localStorage
 * от конфигураций, которые не использовались заданное время.
 */
export function cleanupStaleConfigs(maxAgeMs: number = CONFIG_STALE_MAX_AGE): void {
  const now = Date.now();

  const keys = Object.keys(localStorage).filter((key) => key.startsWith(STORAGE_PREFIX));

  keys.forEach((key) => {
    try {
      const raw = localStorage.getItem(key);

      if (!raw) {
        return;
      }

      const entry = JSON.parse(raw);
      const lastUsedAt = entry?.data?.meta?.lastUsedAt;

      if (!lastUsedAt || now - lastUsedAt > maxAgeMs) {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
  });
}
